import React, {
  FC,
  memo,
  ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import AccessRights from "../../../../components/AccessRights/AccessRights";
import Icon from "../../../../components/Icon/Icon";
import StatusTag from "../../../../components/StatusTag/StatusTag";
import { Span } from "../../../../components/Text/Text";
import { ProjectT } from "../../../../models/models";
import { useAppSelector } from "../../../../store/hooks";
import { selectCurrentUser } from "../../../../store/selectors/authSelectors";
import BroadcasterLogo from "../BroadcasterLogo/BroadcasterLogo";
import { useNonDesktopViewport } from "../../../../utils/hooks/useNonDesktopViewport";
import { DATE_FORMAT } from "../../../../utils/constants";
import {
  selectCurrentLang,
  selectDirection,
} from "../../../../store/selectors/appSelectors";
import Tooltip from "../../../../components/Tooltip/Tooltip";
import { useTranslate } from "../../../../utils/hooks/useTranslate";
import {
  IconColorE,
  LangE,
  StatusE,
  UserRoleE,
} from "../../../../models/enums";
import { useCopyText } from "../../../../utils/helpers/copyText";
import useDeliveryActions from "../../../../utils/hooks/useDeliveryActions";
import AdAgencyName from "../../../CampaignList/AdAgencyName";
import AdvertiserName from "../../../CampaignList/AdvertiserName";
import BrandName from "../../../CampaignList/BrandName";
import { Swiper, SwiperSlide } from "swiper/react";
import { statusFilters } from "../../../../utils/helpers/statusFilters";
import SwipeImage from "../../../../components/SwipeImage/SwipeImage";
import getCampaignNameWithoutDate from "../../../../utils/helpers/getCampaignNameWithoutDate";
import RedDot from "../../../../components/RedDot/RedDot";
import DollarSign from "../../../../components/DollarSign/DollarSign";
import ReDeliveryIndicator from "../../../../components/ReDeliveryIndicator/ReDeliveryIndicator";
import TimerIcon from "../../../../components/TimerIcon/TimerIcon";

import styles from "./styles.module.scss";

type ProjectListItemShortPropTypes = {
  project: ProjectT;
  index: number;
  paginationElement: ReactElement;
  isLastItem: boolean;
  setShowContextMenuIndex: (value: any) => void;
  showContextMenuIndex: number;
  setSize: (index: number, size: number) => void;
  windowWidth: number;
};

const MAX_SHOWN_BROADCASTERS_MOBILE = 3;
const MAX_SHOWN_BROADCASTERS_DESKTOP = 20;
const SWIPE_STORAGE_ITEM_NAME = "swipeDelivery";

const ProjectListItemShort: FC<ProjectListItemShortPropTypes> = memo(
  (props) => {
    const {
      project,
      index,
      paginationElement,
      isLastItem,
      setShowContextMenuIndex,
      showContextMenuIndex,
      setSize,
      windowWidth,
    } = props;
    const { t } = useTranslate();
    const isNonDesktop = useNonDesktopViewport();
    const isViewportTablet =
      windowWidth >= 770 && windowWidth <= 991;
    const maxShownBroadcasters = isNonDesktop
      ? MAX_SHOWN_BROADCASTERS_MOBILE
      : MAX_SHOWN_BROADCASTERS_DESKTOP;
    const user = useAppSelector(selectCurrentUser);
    const direction = useAppSelector(selectDirection);
    const lang = useAppSelector(selectCurrentLang);
    const navigate = useNavigate();
    const copyText = useCopyText();
    const broadcastersAmount = useMemo(
      () => project?.BroadcastersLogoInfo?.length || 0,
      [project?.BroadcastersLogoInfo]
    );
    const { defaultActions, hasAvailableActions } = useDeliveryActions(
      project,
      () => {}
    ); // actions for icons
    const [showActionList, setShowActionList] = useState(false); // desktop
    const statusFilter =
      project &&
      statusFilters.find(({ value }) => value === project?.StatusName);
    const [isSwipeShown, setIsSwipeShown] = useState<boolean>(
      !!localStorage.getItem(SWIPE_STORAGE_ITEM_NAME)
    );
    const liRef = useRef<HTMLLIElement>();

    useEffect(() => {
      if (liRef.current) {
        setSize(index, liRef.current.getBoundingClientRect().height);
      }
    }, [setSize, index, liRef.current, windowWidth]);

    const toggleShowContextMenu = () => {
      setShowContextMenuIndex((prev) => (prev === index ? -1 : index));
    };

    const navigateToProjectPage = (e) => {
      e.preventDefault();
      navigate(`/deliveries/${project.Id}`);
    };

    const navigateToCampaignPage = (e) => {
      e.preventDefault();
      navigate(`/campaigns/${project.campaign.Id}`);
    };

    const Destinations = () => (
      <ul className={styles.destinations}>
        {project?.BroadcastersLogoInfo?.filter(({ BroadcasterId }) =>
          user?.role !== UserRoleE.Broadcaster
            ? true
            : +user?.id === BroadcasterId
        )
          ?.slice(0, maxShownBroadcasters)
          ?.map(({ BroadcasterId, BroadcasterName }) => (
            <li key={BroadcasterId + project?.Id}>
              <BroadcasterLogo id={+BroadcasterId} name={BroadcasterName} />
            </li>
          ))}
        {broadcastersAmount > maxShownBroadcasters && (
          <li className={styles.plusBroadcastersItem}>
            +{broadcastersAmount - maxShownBroadcasters}
          </li>
        )}
      </ul>
    );

    const handleSwipeChange = () => {
      if (!isSwipeShown) {
        localStorage.setItem(SWIPE_STORAGE_ITEM_NAME, "true");
        setIsSwipeShown(true);
      }
    };

    return isNonDesktop ? (
      <li dir={direction} ref={liRef} style={{ position: "relative" }}>
        {showContextMenuIndex === index && hasAvailableActions && (
          <ul className={styles.contextMenu}>
            {defaultActions
              .filter(({ action }) => action !== null)
              .map((action, index) => (
                <AccessRights
                  accessFor={action.accessFor}
                  condition={action.condition}
                  key={index}
                >
                  <li
                    onClick={() => {
                      if (action.icon === "sendBroadcast") {
                        action.action(project?.campaign?.Id);
                      } else {
                        action.action();
                      }
                    }}
                  >
                    <Icon
                      name={action.icon}
                      alt={action.name}
                      title={action.name}
                    />
                    <Span>{action.name}</Span>
                  </li>
                </AccessRights>
              ))}
          </ul>
        )}
        <div className={styles.wrapperMobile}>
          <span
            onClick={navigateToProjectPage}
            className={styles.deliveryNumberLinkMobile}
          >
            <span
              className={styles.deliveryNumber}
              style={{ backgroundColor: statusFilter?.color }}
              id={index === 0 ? "first-delivery-number" : ""}
            >
              #{project?.Number}
            </span>
            <span className={styles.deliveryName}>
              {" "}
              {getCampaignNameWithoutDate(
                project?.ProjectComment || project?.campaign?.Name
              ) || <Span>No description</Span>}
            </span>
          </span>
          {hasAvailableActions && (
            <div className={styles.iconsMobile}>
              <Icon name="menu" onClick={toggleShowContextMenu} />
            </div>
          )}
          <Swiper
            onSlideChange={handleSwipeChange}
            slidesPerView={isViewportTablet ? 2 : 1}
          >
            <SwiperSlide>
              {index === 0 && !isSwipeShown && <SwipeImage />}
              <ul className={styles.projectInfoMobile}>
                <li className={styles.campaignLinkMobile}>
                  <Span>Campaign</Span>:{" "}
                  <span onClick={navigateToCampaignPage}>
                    {project?.campaign?.Id}{" "}
                    {project?.campaign?.Name !== project?.Name &&
                      project?.campaign?.Name}
                  </span>
                </li>
                <li>
                  <AdvertiserName campaign={project} />
                </li>
                <li>
                  <BrandName campaign={project} />
                </li>
                <li>
                  <AdAgencyName campaign={project} />
                </li>
                <li>
                  <Span>Broadcasting date</Span>:{" "}
                  {dayjs(project?.BroadcastingDate).format(DATE_FORMAT)}
                </li>
              </ul>
            </SwiperSlide>
            <SwiperSlide>
              <ul className={styles.projectInfoMobile}>
                <li>
                  <Span>Post-Production</Span>:{" "}
                  {project?.PostProductionCompanyName}
                </li>
                <li>
                  <Span>Production Company</Span>:{" "}
                  {project?.ProductionCompanyName || t`NONE`}
                </li>
                <li>
                  <Span>Market category</Span>:{" "}
                  {(lang === LangE.EN
                    ? project?.industry?.SectorParentNameEN
                    : project?.industry?.SectorParentNameHE) || "N/A"}
                  /
                  {(lang === LangE.EN
                    ? project?.industry?.DisplayNameEn
                    : project?.industry?.DisplayNameHe) || "N/A"}
                </li>
                <li>
                  <Span>Creation date</Span>:{" "}
                  {dayjs(project?.CreationDateTime).format(DATE_FORMAT)}
                </li>
              </ul>
            </SwiperSlide>
          </Swiper>
          <div className={styles.destinationsWrapperMobile}>
            <Destinations />
            <span className={styles.versionsCountMobile}>
              {project.VersionCount}{" "}
              {project.VersionCount !== 1 ? (
                <Span>Versions</Span>
              ) : (
                <Span>Version</Span>
              )}
            </span>
          </div>
        </div>
        {isLastItem && paginationElement}
      </li>
    ) : (
      <li className={styles.wrapper} dir={direction} ref={liRef}>
        <div className={styles.topRow}>
          <div className={styles.deliveryTitle}>
            <div>
              <span className={styles.deliveryLinkWrapper}>
                <Icon
                  name="copy"
                  color={IconColorE.BLUE}
                  className={styles.copyNumberIcon}
                  onClick={() => copyText(project.Number as unknown as string)}
                />
                <span>
                  <Span>Delivery</Span>:
                </span>
                <Tooltip
                  text={t`Go to the delivery page` + `: ${project.Name}`}
                  withoutTranslation
                >
                  <a
                    href={`/deliveries/${project.Id}`}
                    onClick={navigateToProjectPage}
                    className={styles.deliveryLink}
                  >
                    <span id={index === 0 ? "first-delivery-number" : ""}>
                      #{project?.Number}{" "}
                    </span>
                    <span>
                      {getCampaignNameWithoutDate(project?.ProjectComment)}
                    </span>
                  </a>
                </Tooltip>
                <DollarSign
                  condition={!!project?.DeliveryStatus}
                  title={project?.DeliveryStatus}
                />
                <ReDeliveryIndicator
                  isReDelivery={project?.IsReDelivery}
                  sourceNumber={project?.SourceDeliveryNumber}
                  href={
                    project?.SourceProjectId
                      ? `/deliveries/${project.SourceProjectId}`
                      : null
                  }
                  type="delivery"
                />
              </span>
              <RedDot isReactProject={project?.IsReactProject} />
            </div>
            <div className={styles.deliveryNumberAndVersions}>
              <span className={styles.campaignLinkWrapper}>
                <span>
                  <Span style={{ fontSize: ".87rem" }}>Campaign</Span>:
                </span>
                <a
                  href={`/campaigns/${project?.campaign?.Id}`}
                  onClick={navigateToCampaignPage}
                  className={styles.campaignLink}
                >
                  <span id={index === 0 ? "first-delivery-number" : ""}>
                    {project?.campaign?.Id}
                  </span>
                  <span className={styles.campaignName}>
                    {" "}
                    {getCampaignNameWithoutDate(project?.campaign.Name)}
                  </span>
                </a>
              </span>
              <span className={styles.versionsCount}>
                <Span>Versions</Span> ({project.VersionCount})
                {project?.ProjectFiles?.some(
                  (v) => v.NotSuitableForBroadcast
                ) ? (
                  <Icon
                    name="exclamation"
                    title="Some of the versions are not suitable for broadcasting"
                    className={styles.notSuitableWarningIcon}
                  />
                ) : null}
              </span>
            </div>
          </div>
          <Destinations />
          <div className={styles.desktopStatusWrapper}>
            <TimerIcon
              statusName={project.StatusName}
              clientType={user.clientType}
              role={user.role}
            />
            <StatusTag
              status={project.StatusName as string}
              isRemovable={false}
            />
            {hasAvailableActions && (
              <div
                className={styles.iconsContextMenu}
                onMouseOver={() => setShowActionList(true)}
                onMouseLeave={() => setShowActionList(false)}
              >
                <Icon name="menu" className={styles.actionsIcon} />
                {showActionList && (
                  <ul
                    className={`${
                      // add offset for first item if long list
                      !index &&
                      user.role === UserRoleE.AdvertisingAgency &&
                      project.StatusName ===
                        StatusE.WaitingForApprovalOfAdvertisingAgency
                        ? styles.firstItem
                        : ""
                    } ${
                      // media-company menu opens below the three-dots, not above
                      user.role === UserRoleE.MediaCompany ? styles.belowMenu : ""
                    }`}
                    dir={direction}
                  >
                    {defaultActions
                      .filter(({ action }) => action !== null)
                      .map((action, index) => (
                        <AccessRights
                          accessFor={action.accessFor}
                          condition={action.condition}
                          key={index}
                        >
                          <li
                            onClick={() => {
                              if (action.icon === "sendBroadcast") {
                                action.action(project?.campaign?.Id ?? project?.Campaign?.Id);
                              } else {
                                action.action();
                              }
                            }}
                          >
                            <Icon
                              name={action.icon}
                              alt={action.name}
                              onClick={action.action}
                            />
                            <Span>{action.name}</Span>
                          </li>
                        </AccessRights>
                      ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
        <div className={styles.middleRow}>
          <span>
            <AdvertiserName campaign={project} />
          </span>
          <span>
            <BrandName campaign={project} />
          </span>
          <span className={styles.adAgencySpan}>
            <AdAgencyName campaign={project} />
          </span>
          <span>
            <Span>Broadcasting date</Span>:{" "}
            {dayjs(project.BroadcastingDate).format(DATE_FORMAT)}
          </span>
          <span title={project.PostProductionCompanyName}>
            <Span>Post-Production</Span>: {project.PostProductionCompanyName}
          </span>
          <span title={project.ProductionCompanyName || t`NONE`}>
            <Span>Production Company</Span>:{" "}
            {project.ProductionCompanyName || t`NONE`}
          </span>
          <span
            title={`${t`Market category`}: ${
              (lang === LangE.EN
                ? project.industry.SectorParentNameEN
                : project.industry.SectorParentNameHE) || "N/A"
            }/${
              (lang === LangE.EN
                ? project.industry.DisplayNameEn
                : project.industry.DisplayNameHe) || "N/A"
            }`}
          >
            <Span>Market category</Span>:{" "}
            {!project.industry.SectorParentNameEN &&
            !project.industry.DisplayNameEn
              ? "N/A"
              : `${
                  (lang === LangE.EN
                    ? project.industry.SectorParentNameEN
                    : project.industry.SectorParentNameHE) || "- "
                }/${
                  (lang === LangE.EN
                    ? project.industry.DisplayNameEn
                    : project.industry.DisplayNameHe) || " -"
                }`}
          </span>
          <span>
            <Span>Creation date</Span>:{" "}
            {dayjs(project.CreationDateTime).format(DATE_FORMAT)}
          </span>
        </div>
        <div className={styles.mobileStatusWrapper}>
          <TimerIcon
            statusName={project.StatusName}
            clientType={user.clientType}
            role={user.role}
          />
          <StatusTag
            status={project.StatusName as string}
            isRemovable={false}
          />
        </div>
      </li>
    );
  }
);

export default ProjectListItemShort;
